import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import BLOG_POSTS from "../data/blogPosts.json";

const LOGO = "https://customer-assets.emergentagent.com/job_zenith-reserve/artifacts/pq79qyzz_84d55109-d543-4a1a-9205-51b665e4d9d0.webp";

const links = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Treatments" },
    { to: "/hair-restoration", label: "Hair Program" },
    { to: "/memberships", label: "Memberships" },
    { to: "/financing", label: "Financing" },
    { to: "/shop", label: "Shop" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Blog" },
    { to: "/faqs", label: "FAQs" },
    { to: "/contact", label: "Contact" },
];

const PRIMARY = ["/services", "/memberships", "/hair-restoration", "/blog", "/about", "/contact"];
const primaryLinks = PRIMARY.map((to) => links.find((l) => l.to === to));
const moreLinks = links.filter((l) => l.to !== "/" && !PRIMARY.includes(l.to));
const testId = (l) => `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;

function MoreMenu({ textCol, goTo, pathname }) {
    const [open, setOpen] = React.useState(false);
    const ref = React.useRef(null);
    const active = moreLinks.some((l) => l.to === pathname);
    React.useEffect(() => {
        if (!open) return;
        const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
        const onKey = (e) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);
    return (
        <div ref={ref} className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-expanded={open}
                data-testid="nav-more-btn"
                className={`inline-flex items-center gap-1.5 px-3 py-3 text-[11px] tracking-[0.22em] uppercase transition-opacity ${textCol} ${active || open ? "opacity-100" : "opacity-75 hover:opacity-100"}`}
            >
                More <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <div
                data-testid="nav-more-menu"
                className={`absolute right-0 top-full pt-2 transition-[opacity,transform] duration-200 ${open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1 pointer-events-none"}`}
            >
                <div className="min-w-[190px] bg-[#F2EDE2] border border-[#A89070]/25 shadow-[0_18px_40px_-18px_rgba(38,36,34,0.35)] py-2">
                    {moreLinks.map((l) => (
                        <Link
                            key={l.to}
                            to={l.to}
                            onClick={(e) => { setOpen(false); goTo(l.to)(e); }}
                            data-testid={testId(l)}
                            className={`block px-5 py-3 text-[11px] tracking-[0.22em] uppercase hover:bg-[#E6DEC9]/70 hover:text-[#A89070] transition-colors ${pathname === l.to ? "text-[#A89070]" : "text-[#262422]"}`}
                        >
                            {l.label}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Navbar() {
    const [open, setOpen] = React.useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const isHome = location.pathname === "/";
    const isBlogPost = BLOG_POSTS.some((p) => location.pathname === `/${p.slug}`);
    const [scrolled, setScrolled] = React.useState(false);

    React.useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Robust go-to: navigate if different path, otherwise just scroll to top.
    const goTo = (to) => (e) => {
        e.preventDefault();
        if (location.pathname !== to) {
            navigate(to);
        }
        // Always scroll to top — covers both "already on page" and "navigated".
        requestAnimationFrame(() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
        });
        setOpen(false);
    };

    const onDark = isHome && !scrolled;
    const textCol = onDark ? "text-white" : "text-[#262422]";
    const navBg = onDark
        ? "bg-gradient-to-b from-black/55 to-transparent backdrop-blur-[2px]"
        : "bg-[#F2EDE2]/95 backdrop-blur-xl border-b border-black/10";

    return (
        <header
            data-testid="site-navbar"
            className={`fixed inset-x-0 z-50 transition-[background-color,backdrop-filter,border-color,color] duration-500 ${navBg}`}
            style={{ top: "var(--banner-h, 0px)" }}
        >
            <div className="max-w-[1500px] mx-auto px-5 md:px-10 lg:px-14 h-20 md:h-24 flex items-center justify-between gap-6">
                <Link to="/" onClick={goTo("/")} data-testid="brand-logo" className="flex items-center gap-3 shrink-0">
                    <img
                        src={LOGO}
                        alt="Zenith Wellness — Home"
                        className="h-11 md:h-14 w-auto"
                    />
                </Link>

                <nav className="hidden xl:flex items-center gap-1 2xl:gap-3">
                    {primaryLinks.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            onClick={goTo(l.to)}
                            data-testid={testId(l)}
                            className={({ isActive }) =>
                                `relative px-3 py-3 text-[11px] tracking-[0.22em] uppercase transition-colors cursor-pointer ${textCol} ${
                                    isActive || (l.to === "/blog" && isBlogPost) ? "opacity-100" : "opacity-75 hover:opacity-100"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <MoreMenu textCol={textCol} goTo={goTo} pathname={location.pathname} />
                </nav>

                <div className="hidden xl:flex items-center gap-4 shrink-0">
                    <a
                        href="tel:8589054117"
                        data-testid="header-phone"
                        className={`text-[12px] tracking-wider opacity-75 hover:opacity-100 transition ${textCol}`}
                    >
                        858 905 4117
                    </a>
                    <Link
                        to="/services"
                        data-testid="header-book-btn"
                        className={`rounded-full px-6 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold transition-all ${
                            onDark
                                ? "bg-white text-[#262422] hover:bg-[#A89070] hover:text-white"
                                : "bg-[#262422] text-[#F2EDE2] hover:bg-[#A89070] hover:text-white"
                        }`}
                    >
                        Book Now
                    </Link>
                </div>

                <button
                    onClick={() => setOpen(!open)}
                    aria-label="menu"
                    data-testid="mobile-menu-btn"
                    className={`xl:hidden w-11 h-11 rounded-full border flex items-center justify-center transition ${
                        onDark ? "border-white/40 text-white" : "border-black/20 text-black"
                    }`}
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu-panel"
                    className="xl:hidden border-t border-black/10 bg-[#F2EDE2] max-h-[calc(100vh-5rem)] overflow-y-auto"
                >
                    <div className="px-6 py-7 flex flex-col gap-4">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                onClick={goTo(l.to)}
                                end={l.to === "/"}
                                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="serif text-[28px] text-[#262422] leading-tight"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/services"
                            onClick={() => setOpen(false)}
                            data-testid="mobile-book-btn"
                            className="mt-4 text-center bg-[#262422] text-[#A89070] rounded-full px-6 py-4 text-xs uppercase tracking-[0.25em] font-medium"
                        >
                            Book Now
                        </Link>
                        <a href="tel:8589054117" className="text-center text-sm text-[#544638]">
                            Call · 858 905 4117
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
